import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  PanelBarComponent,
  PanelBarItemModel,
  PanelBarItemTemplateDirective
} from '@progress/kendo-angular-layout';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { clientServiceEndpoints } from 'src/app/helpers/urls';
import { ClientDto } from 'src/app/models/client/client-dto';
import { ProcessDto } from 'src/app/models/process/process-dto';
import { TaskDto } from 'src/app/models/process/task-dto';
import { TaskQaFormItemType } from 'src/app/models/process/task-qa-form-item-type';
import { SearchListConfig } from 'src/app/models/search-list-config';
import { CreateQaWorkItemDto } from 'src/app/models/work/qa/create-qa-work-item-dto';
import { QaFormFileDto } from 'src/app/models/work/qa/qa-form-file-dto';
import { WorkResponsibleType } from 'src/app/models/work/work-responsible/work-responsible-type-enum';
import { AlertService } from 'src/app/services/alert.service';
import { UserMicroservice } from 'src/app/services/api/user-api';
import { WorkService } from 'src/app/services/api/work-api';
import { ModalOpenerService } from 'src/app/services/modal-opener.service';
import { environment } from 'src/environments/environment';
import { SearchableListMicroComponent } from '../../../features/searchable-list-micro/searchable-list-micro.component';
import { WorkItemsQaReportClientInfo } from '../work-items-qa-report-client/model/work-items-qa-report-client-info';
import { WorkItemsQaReportClientComponent } from '../work-items-qa-report-client/work-items-qa-report-client.component';
import { workItemsQaReportConfirmationOpener } from '../work-items-qa-report-confirmation/work-items-qa-report-confirmation.modal';
import { QaFormExchange } from '../work-items-qa-report-form/model/qa-form-exchange';
import { QaFormItemWrapperUpload } from '../work-items-qa-report-form/model/qa-form-item-wrapper';
import { QaFormWrapper } from '../work-items-qa-report-form/model/qa-form-wrapper';
import { WorkItemsQaReportFormComponent } from '../work-items-qa-report-form/work-items-qa-report-form.component';
import { WorkItemsQaReportTargetTaskSelectionComponent } from '../work-items-qa-report-target-task-selection/work-items-qa-report-target-task-selection.component';
import { QaReportTaskExchange } from '../work-items-qa-report-task/model/qa-report-task-exchange';
import { WorkItemsQaReportTaskComponent } from '../work-items-qa-report-task/work-items-qa-report-task.component';
import { WORK_ITEMS_QA_GENERIC_REPORT_DATA } from './work-items-qa-generic-report.modal';

enum PanelItemType {
  Client = 1,
  Task = 2,
  Form = 3
}

@Component({
  templateUrl: './work-items-qa-generic-report.component.html',
  styleUrl: './work-items-qa-generic-report.component.scss',
  imports: [
    TooltipDirective,
    PanelBarComponent,
    PanelBarItemTemplateDirective,
    SearchableListMicroComponent,
    WorkItemsQaReportClientComponent,
    WorkItemsQaReportTargetTaskSelectionComponent,
    WorkItemsQaReportTaskComponent,
    WorkItemsQaReportFormComponent
  ]
})
export class WorkItemsQaGenericReportComponent implements OnInit {
  protected readonly activeModal = inject(NgbActiveModal);
  protected readonly inputData = inject(WORK_ITEMS_QA_GENERIC_REPORT_DATA);
  private readonly alertService$ = inject(AlertService);
  private workService = inject(WorkService);
  private userService = inject(UserMicroservice);
  private readonly modalOpener = inject(ModalOpenerService);
  private readonly destroyRef = inject(DestroyRef);

  panelItems: PanelBarItemModel[] = [];

  private clientPanelItem: PanelBarItemModel;
  private taskPanelItem: PanelBarItemModel;
  private formPanelItem: PanelBarItemModel;

  selectedItems: ClientDto[];
  clientsConfig: SearchListConfig;
  clientsUrl: string = clientServiceEndpoints.client;

  qaTask: TaskDto;

  clientInfo: WorkItemsQaReportClientInfo;

  selectedTasks: TaskDto[];
  selectedProcesses: ProcessDto[];
  selectedProcessNodeId: number;

  qaFormExchange: QaFormExchange;
  qaReportTaskExchange: QaReportTaskExchange;

  constructor() {
    this.clientPanelItem = <PanelBarItemModel>{
      title: 'Client information',
      content: PanelItemType.Client,
      expanded: true
    };

    this.taskPanelItem = <PanelBarItemModel>{
      title: 'QA work item configuration',
      content: PanelItemType.Task,
      expanded: true
    };

    this.formPanelItem = <PanelBarItemModel>{
      title: 'QA form',
      content: PanelItemType.Form,
      disabled: true
    };

    this.panelItems.push(this.clientPanelItem);
    this.panelItems.push(this.taskPanelItem);
    this.panelItems.push(this.formPanelItem);

    this.clientsConfig = new SearchListConfig();
    this.clientsConfig.virtual = true;
    this.clientsConfig.placeholder = 'Search client';
    this.clientsConfig.label = 'Client';
    this.clientsConfig.key = 'clients';
    this.clientsConfig.order = '1';
    this.clientsConfig.noAccessLabel =
      'No access to clients that match search criteria';
    this.clientsConfig.mode = 'none';
    //this.clientsConfig.maxHeight = 20;
  }

  ngOnInit(): void {
    this.qaFormExchange = new QaFormExchange();
    this.qaReportTaskExchange = new QaReportTaskExchange();
  }

  createAndCopyLink(): void {
    const link = `${environment.uiEndpoint}workflow/work-items?genericQaHyperlink=true`;
    void navigator.clipboard.writeText(link);
    this.alertService$.showSuccess(`Copied link: ${link}`);
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  save(): void {
    const qaForm = this.qaFormExchange.qaForm;

    const createDto = new CreateQaWorkItemDto();
    createDto.linkedProcessNodeId = this.selectedProcessNodeId;
    createDto.qaTaskId = qaForm.taskId;
    createDto.qaFormId = qaForm.id;
    createDto.clientId = this.currentClient?.id;
    createDto.checklistId = this.qaReportTaskExchange.checklist?.id;
    createDto.workResponsibleId = this.qaReportTaskExchange.workResponsible?.id;
    createDto.workResponsibleType =
      this.qaReportTaskExchange.workResponsible?.type;
    createDto.hour = this.qaReportTaskExchange.dueTime?.getHours();
    createDto.minute = this.qaReportTaskExchange.dueTime?.getMinutes();
    createDto.hourEstimate =
      this.qaReportTaskExchange.timeEstimateValue?.getHours();
    createDto.minuteEstimate =
      this.qaReportTaskExchange.timeEstimateValue?.getMinutes();

    const uploadItems = qaForm.items
      .filter(item => item.type == TaskQaFormItemType.Upload)
      .map(item => item as QaFormItemWrapperUpload);
    const qaFormFiles: QaFormFileDto[] = QaFormWrapper.createQaFormFileDtos(
      qaForm.id,
      uploadItems
    );

    createDto.formItems =
      qaForm.items.map(item => QaFormWrapper.createFrom(item, qaFormFiles)) ??
      [];

    createDto.isUrgent = qaForm.isUrgent;
    if (qaForm.submitterAsResponsible) {
      createDto.workResponsibleId = this.userService.getSelfId();
      createDto.workResponsibleType = WorkResponsibleType.User;
    }

    this.workService
      .createQaWorkItem$(createDto, qaFormFiles)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(_result => {
        //this.alertService.showSuccess('Success', 'QA report sent');
        this.activeModal.close();
        // TODO: maybe send onWorkItemQaReportCreated event to the work-items grid

        this.modalOpener.open(workItemsQaReportConfirmationOpener, {
          clientInfo: this.clientInfo,
          qaTask: this.qaTask
        });
      });
  }

  get canSave(): boolean {
    const clientSelected = !!this.clientInfo;
    const selectionIsValid = !!this.selectedProcessNodeId;
    const formIsValid = this.qaFormExchange?.qaForm?.computeIsValid();
    return clientSelected && formIsValid && selectionIsValid;
  }

  isClient(dataItem: PanelBarItemModel): boolean {
    return dataItem.content == PanelItemType.Client;
  }

  isTask(dataItem: PanelBarItemModel): boolean {
    return dataItem.content == PanelItemType.Task;
  }

  isForm(dataItem: PanelBarItemModel): boolean {
    return dataItem.content == PanelItemType.Form;
  }

  selectedTasksChanged(tasks: TaskDto[]): void {
    this.selectedTasks = tasks;
  }

  selectedProcessChanged(processes: ProcessDto[]): void {
    this.selectedProcesses = processes;
  }

  selectedProcessNodeIdChanged(processNodeId: number): void {
    this.selectedProcessNodeId = processNodeId;
  }

  taskSelected(qaTask: TaskDto): void {
    this.qaTask = qaTask;
    if (!qaTask) {
      this.formPanelItem.expanded = false;
      this.formPanelItem.disabled = true;
      return;
    }
    this.formPanelItem.expanded = true;
    this.formPanelItem.disabled = false;
  }

  currentClient: ClientDto;
  changeFilter(clients: ClientDto[]): void {
    if (clients?.length == 1) {
      this.currentClient = clients[0];
    } else if (clients?.length > 1) {
      this.currentClient = clients.find(c => c.id != this.currentClient.id);
    } else {
      this.currentClient = null;
    }

    if (this.currentClient) {
      this.selectedItems = [this.currentClient];
      this.clientsConfig.placeholder = `${this.currentClient.name} (${this.currentClient.no}) selected`;
    } else {
      this.selectedItems = [];
      this.clientsConfig.placeholder = 'Search client';
    }

    this.initClientInfo();
  }

  private initClientInfo(): void {
    if (this.currentClient) {
      this.clientInfo = new WorkItemsQaReportClientInfo(
        this.currentClient.no,
        this.currentClient.name,
        this.currentClient.group,
        this.currentClient.organizationHierarchyNames
      );
    } else {
      this.clientInfo = null;
    }
  }
}
